import {
  trimDatabaseWhitespace,
  type ApiErrorResponse,
  type Contribution,
  type ContributionMutationOutcome,
  type ContributionMutationResponse,
  type ContributionResponse,
  type LeaderboardItem,
  type LeaderboardResponse,
} from "../shared/api-contract";

type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export const DEFAULT_CONTRIBUTION_REQUEST_TIMEOUT_MS = 30_000;

export interface ContributionRequestOptions {
  timeoutMs?: number;
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSafeNonnegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isNullableHttpsUrl(value: unknown): value is string | null {
  if (value === null) return true;
  if (typeof value !== "string") return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function isLeaderboardItem(value: unknown): value is LeaderboardItem {
  return isRecord(value)
    && typeof value.id === "string"
    && typeof value.name === "string"
    && isSafeNonnegativeInteger(value.voteCount)
    && isSafeNonnegativeInteger(value.rank)
    && value.rank > 0
    && isNullableHttpsUrl(value.imageUrl);
}

function parseLeaderboard(value: unknown): LeaderboardResponse {
  if (
    !isRecord(value)
    || !Array.isArray(value.items)
    || !value.items.every(isLeaderboardItem)
    || !isSafeNonnegativeInteger(value.totalVotes)
    || !isSafeNonnegativeInteger(value.matchCount)
  ) {
    throw new ApiRequestError(502, "invalid_response", "The server returned an invalid leaderboard.");
  }
  return value as unknown as LeaderboardResponse;
}

function isContribution(value: unknown): value is Contribution {
  if (!isRecord(value) || !isRecord(value.item)) return false;
  return typeof value.item.id === "string"
    && typeof value.item.name === "string"
    && isNullableHttpsUrl(value.item.imageUrl)
    && typeof value.castAt === "string"
    && !Number.isNaN(Date.parse(value.castAt));
}

function parseContribution(value: unknown): Contribution | null {
  if (!isRecord(value) || !("contribution" in value)) {
    throw new ApiRequestError(502, "invalid_response", "The server returned an invalid contribution.");
  }
  const response = value as unknown as ContributionResponse;
  if (response.contribution !== null && !isContribution(response.contribution)) {
    throw new ApiRequestError(502, "invalid_response", "The server returned an invalid contribution.");
  }
  return response.contribution;
}

function parseContributionMutation(value: unknown): ContributionMutationResponse {
  if (
    !isRecord(value)
    || !isContribution(value.contribution)
    || !["created", "replayed", "already_cast"].includes(
      value.outcome as ContributionMutationOutcome,
    )
  ) {
    throw new ApiRequestError(502, "invalid_response", "The server returned an invalid contribution.");
  }
  return value as unknown as ContributionMutationResponse;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new ApiRequestError(response.status || 502, "invalid_response", "The server returned an invalid response.");
  }
}

async function expectOk(response: Response): Promise<unknown> {
  const body = await readJson(response);
  if (response.ok) return body;
  const error = body as Partial<ApiErrorResponse>;
  const code = error.error?.code ?? "request_failed";
  const message = error.error?.message ?? "The request could not be completed.";
  throw new ApiRequestError(response.status, code, message);
}

async function requestContribution(
  request: Fetcher,
  input: RequestInfo | URL,
  init: RequestInit,
  options: ContributionRequestOptions,
): Promise<Response> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CONTRIBUTION_REQUEST_TIMEOUT_MS;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new RangeError("Contribution request timeout must be a positive finite number.");
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await request(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

export async function fetchLeaderboard(
  query: string,
  request: Fetcher = fetch,
): Promise<LeaderboardResponse> {
  return requestLeaderboard(query, false, request);
}

export async function fetchSuggestions(
  query: string,
  request: Fetcher = fetch,
  signal?: AbortSignal,
): Promise<LeaderboardResponse> {
  return requestLeaderboard(query, true, request, signal);
}

async function requestLeaderboard(
  query: string,
  suggestions: boolean,
  request: Fetcher,
  signal?: AbortSignal,
): Promise<LeaderboardResponse> {
  const trimmed = trimDatabaseWhitespace(query);
  const parameters = new URLSearchParams();
  if (trimmed !== "") parameters.set("q", trimmed);
  if (suggestions) parameters.set("suggest", "1");
  const url = parameters.size === 0
    ? "/api/leaderboard"
    : `/api/leaderboard?${parameters.toString()}`;
  const response = await request(url, {
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  return parseLeaderboard(await expectOk(response));
}

export async function fetchContribution(
  accessToken: string,
  request: Fetcher = fetch,
  options: ContributionRequestOptions = {},
): Promise<Contribution | null> {
  const response = await requestContribution(request, "/api/me/contribution", {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  }, options);
  return parseContribution(await expectOk(response));
}

export async function submitExistingContribution(
  accessToken: string,
  itemId: string,
  idempotencyKey: string,
  request: Fetcher = fetch,
  options: ContributionRequestOptions = {},
): Promise<ContributionMutationResponse> {
  const response = await requestContribution(request, "/api/contributions", {
    method: "POST",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ itemId, idempotencyKey }),
  }, options);
  return parseContributionMutation(await expectOk(response));
}

export async function submitCreatedContribution(
  accessToken: string,
  name: string,
  image: Blob,
  idempotencyKey: string,
  request: Fetcher = fetch,
  options: ContributionRequestOptions = {},
): Promise<ContributionMutationResponse> {
  const form = new FormData();
  form.append("name", name);
  form.append("idempotencyKey", idempotencyKey);
  form.append("image", image, "upload.jpg");
  const response = await requestContribution(request, "/api/items", {
    method: "POST",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: form,
  }, options);
  return parseContributionMutation(await expectOk(response));
}

interface ExistingContributionAttempt {
  accessToken: string;
  itemId: string;
  idempotencyKey: string;
}

interface ExistingContributionRecoveryDependencies {
  cast(
    accessToken: string,
    itemId: string,
    idempotencyKey: string,
  ): Promise<ContributionMutationResponse>;
  read(accessToken: string): Promise<Contribution | null>;
}

function isAmbiguousFailure(error: unknown): boolean {
  if (isRecord(error) && error.name === "AbortError") return true;
  if (error instanceof ApiRequestError) return error.status >= 500;
  if (isRecord(error) && typeof error.status === "number") return error.status >= 500;
  return true;
}

/**
 * Reconciles an ambiguous mutation before retrying. The retry deliberately
 * reuses the original target and idempotency key; callers retain the pending
 * intent until this returns an authoritative result.
 */
export async function castExistingContributionWithReconciliation(
  attempt: ExistingContributionAttempt,
  dependencies: ExistingContributionRecoveryDependencies = {
    cast: submitExistingContribution,
    read: fetchContribution,
  },
): Promise<ContributionMutationResponse> {
  return reconcileContributionMutation(
    attempt.accessToken,
    () => dependencies.cast(
      attempt.accessToken,
      attempt.itemId,
      attempt.idempotencyKey,
    ),
    dependencies.read,
  );
}

interface CreatedContributionAttempt {
  accessToken: string;
  name: string;
  image: Blob;
  idempotencyKey: string;
}

interface CreatedContributionRecoveryDependencies {
  cast(
    accessToken: string,
    name: string,
    image: Blob,
    idempotencyKey: string,
  ): Promise<ContributionMutationResponse>;
  read(accessToken: string): Promise<Contribution | null>;
}

/** Preserves the exact name, Blob, and request key across an uncertain upload. */
export async function castCreatedContributionWithReconciliation(
  attempt: CreatedContributionAttempt,
  dependencies: CreatedContributionRecoveryDependencies = {
    cast: submitCreatedContribution,
    read: fetchContribution,
  },
): Promise<ContributionMutationResponse> {
  return reconcileContributionMutation(
    attempt.accessToken,
    () => dependencies.cast(
      attempt.accessToken,
      attempt.name,
      attempt.image,
      attempt.idempotencyKey,
    ),
    dependencies.read,
  );
}

async function reconcileContributionMutation(
  accessToken: string,
  cast: () => Promise<ContributionMutationResponse>,
  read: (accessToken: string) => Promise<Contribution | null>,
): Promise<ContributionMutationResponse> {
  try {
    return await cast();
  } catch (error) {
    if (!isAmbiguousFailure(error)) throw error;
    const authoritative = await read(accessToken);
    if (authoritative !== null) {
      return { outcome: "already_cast", contribution: authoritative };
    }
    return cast();
  }
}
